// MACHINERY V2 (Stroje) — V3 table row (M2.13).
//
// One <tr>: STAV · PRIPOJ. · STROJ · LOKÁCIA · AKTUÁLNE · 30 DNÍ · OTV. SR ·
// kebab. Cell bodies live in fleetTableCells.tsx / fleetTableDataCells.tsx; this
// file owns the row shell, the single derivation and the navigation guards.
//
// SINGLE DERIVATION, SAME AS THE CARD AND THE GROUPED ROW: deriveCardPresentation
// is called ONCE here and its flags are handed down. No cell re-tests raw
// telemetry.
//
// ROW NAVIGATION GUARD — MOUSE **AND** KEYBOARD. The row is clickable AND
// keyboard-activatable, so every interactive descendant that could swallow an
// activation stops propagation on BOTH paths. Verified by reading the composed
// handler chain, not assumed:
//   · the kebab trigger stops click (FleetCardMenu.tsx:131) and Enter/Space
//     (FleetCardMenu.tsx:133-135); its portalled content stops click (:160);
//   · the capability badge stops click ALWAYS, interactive or not
//     (CapabilityBadge.tsx:170-173) and Enter/Space (CapabilityBadge.tsx:190-192
//     — stopPropagation only, never preventDefault, or the badge would stop
//     opening its own popup);
//   · the CapTip tooltip content stops click (CapTip.tsx:76);
//   · the readiness popup is mounted at PAGE level (FleetOverlays.tsx), outside
//     every row, and additionally guards Dialog.Overlay AND Dialog.Content.
// The two `title`-only surfaces in the row (the alarm corner triangle and the
// conn dot) are not focusable and fire no activation event, so they need no
// guard.
//
// WHY THE ROW TAKES KEYBOARD ACTIVATION AT ALL, when FleetCard/FleetRow do not:
// a table row has no other affordance to reach the profile, and the two nested
// controls above already guard the keyboard path, so the hazard that would
// normally argue against it does not exist here. The focus ring is in
// machinery-v2-table.scss (`.ms-row:focus-visible`).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { Machine } from "../../../models/models";
import { MachineryV2ReadinessPresentation } from "../../readiness/readinessPresentation";
import FleetCardMenu, { FleetMenuAction } from "../FleetCardMenu";
import { MachineryV2Telemetry, deriveCardPresentation } from "../cardStates";
import { ConnCell, LocationCell, MachineCell, StatusCell } from "./fleetTableCells";
import { CurrentCell, SparklineCell, SrCell } from "./fleetTableDataCells";

export interface FleetTableRowProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  readiness: MachineryV2ReadinessPresentation | null;
  isNew: boolean;
  actions: FleetMenuAction[];
  onOpenProfile: () => void;
  onOpenReadiness: () => void;
}

const FleetTableRow: React.FC<FleetTableRowProps> = ({
  machine, telemetry, readiness, isNew, actions, onOpenProfile, onOpenReadiness,
}) => {
  const { translate } = useTranslation("myMachinery");
  const p = deriveCardPresentation(telemetry);

  return (
    <tr
      className="ms-row"
      data-machine-id={machine.id}
      tabIndex={0}
      onClick={onOpenProfile}
      onKeyDown={(e) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        // Space would scroll the table; Enter would submit nothing, but keeping
        // both symmetric avoids a "why does one key differ" question later.
        e.preventDefault();
        onOpenProfile();
      }}
      style={{
        cursor: "pointer",
        opacity: machine.isDecommissioned ? 0.65 : 1,
      }}
    >
      {/* position:relative is what lets the alarm triangle pin to the cell
          corner without taking column width. */}
      <td style={{ position: "relative" }}>
        <StatusCell presentation={p} telemetry={telemetry} />
      </td>
      <td style={{ textAlign: "center" }}>
        <ConnCell presentation={p} telemetry={telemetry} />
      </td>
      <td>
        <MachineCell
          machine={machine}
          readiness={readiness}
          isNew={isNew}
          onOpenReadiness={onOpenReadiness}
        />
      </td>
      <td>
        <LocationCell machine={machine} />
      </td>
      <td>
        <CurrentCell presentation={p} telemetry={telemetry} />
      </td>
      <td>
        <SparklineCell />
      </td>
      <td style={{ textAlign: "right" }}>
        <SrCell machine={machine} presentation={p} />
      </td>
      <td style={{ textAlign: "center" }}>
        <FleetCardMenu actions={actions} ariaLabel={translate("myMachinery:v2.cardMenu")} />
      </td>
    </tr>
  );
};

export default FleetTableRow;
